import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import KPICard from "@/components/KPICard";
import StatusPill from "@/components/StatusPill";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { AlertTriangle, DollarSign, Clock, FileText, Printer, Mail, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import { InvoiceStatusDialog } from "@/components/InvoiceStatusDialog";

const BUCKET_COLORS = {
    current: "hsl(var(--chart-3))",
    "1_30": "hsl(var(--chart-1))",
    "31_60": "hsl(var(--chart-4))",
    "61_90": "hsl(var(--chart-2))",
    over_90: "hsl(var(--destructive))",
};

const BUCKET_LABELS = {
    current: "Current",
    "1_30": "1–30 days",
    "31_60": "31–60 days",
    "61_90": "61–90 days",
    over_90: "90+ days",
};

export default function ARAging() {
    const [data, setData] = useState(null);
    const [running, setRunning] = useState(false);
    const [statement, setStatement] = useState(null);
    const [statusInvoice, setStatusInvoice] = useState(null);

    const load = () => api.get("/reports/ar-aging").then(({ data }) => setData(data));
    useEffect(() => { load(); }, []);

    const openStatement = async (customerId) => {
        const { data } = await api.get(`/customers/${customerId}/statement`);
        setStatement(data);
    };
    const refreshStatement = async () => {
        if (statement?.customer?.id) {
            const { data } = await api.get(`/customers/${statement.customer.id}/statement`);
            setStatement(data);
        }
        load();
    };

    const runLateFees = async () => {
        setRunning(true);
        try {
            const { data: r } = await api.post("/late-fees/run-now");
            toast.success(`Late-fees cycle complete — ${r.applied} fees applied`);
            load();
        } catch { toast.error("Failed"); }
        finally { setRunning(false); }
    };

    if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

    const chartData = Object.entries(data.buckets).map(([k, v]) => ({
        bucket: BUCKET_LABELS[k] || k, amount: v, key: k,
    }));

    return (
        <div data-testid="ar-aging-page">
            <PageHeader
                title="A/R Aging"
                subtitle="Outstanding receivables by age bucket. Apply late fees, view per-customer statements."
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={runLateFees} disabled={running} data-testid="run-late-fees-btn">
                            <Zap className="w-4 h-4 mr-1" />{running ? "Running…" : "Apply late fees"}
                        </Button>
                    </>
                }
            />
            <div className="px-8 py-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <KPICard testid="ar-total" label="Total A/R" value={fmtMoney(data.total_ar)} icon={DollarSign} delta="All overdue + current" />
                    {Object.entries(data.buckets).map(([k, v]) => (
                        <KPICard key={k} testid={`ar-bucket-${k}`} label={BUCKET_LABELS[k]} value={fmtMoney(v)} icon={k === "over_90" ? AlertTriangle : Clock}
                                 deltaTone={k === "over_90" && v > 0 ? "destructive" : "muted"}
                                 delta={k === "current" ? "Not yet due" : k === "over_90" && v > 0 ? "Escalate" : "—"} />
                    ))}
                </div>

                <Card>
                    <CardContent className="p-6">
                        <div className="overline mb-1">Visual breakdown</div>
                        <h3 className="font-heading text-lg font-semibold mb-6">Aging distribution</h3>
                        <div style={{ width: "100%", height: 240, minHeight: 240 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                                             formatter={(v) => fmtMoney(v)} />
                                    <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                                        {chartData.map((d) => <Cell key={d.key} fill={BUCKET_COLORS[d.key]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-0">
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Per customer</div>
                                <h3 className="font-heading text-lg font-semibold">Outstanding balances</h3>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">{data.by_customer.length} accounts</div>
                        </div>
                        <div className="overflow-x-auto" data-testid="ar-customer-table">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="border-b border-border surface">
                                    <th className="text-left px-4 py-3 overline">Customer</th>
                                    <th className="text-right px-4 py-3 overline">Current</th>
                                    <th className="text-right px-4 py-3 overline">1–30</th>
                                    <th className="text-right px-4 py-3 overline">31–60</th>
                                    <th className="text-right px-4 py-3 overline">61–90</th>
                                    <th className="text-right px-4 py-3 overline">90+</th>
                                    <th className="text-right px-4 py-3 overline">Total</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                                </thead>
                                <tbody>
                                {data.by_customer.map((r) => (
                                    <tr key={r.customer_id} className="border-b border-border hover:bg-secondary/40">
                                        <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                                        <td className="px-4 py-3 text-right tabular text-muted-foreground">{fmtMoney(r.current)}</td>
                                        <td className="px-4 py-3 text-right tabular">{fmtMoney(r["1_30"])}</td>
                                        <td className="px-4 py-3 text-right tabular">{fmtMoney(r["31_60"])}</td>
                                        <td className="px-4 py-3 text-right tabular">{fmtMoney(r["61_90"])}</td>
                                        <td className="px-4 py-3 text-right tabular">
                                            <span className={r.over_90 > 0 ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"}>{fmtMoney(r.over_90)}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right tabular font-semibold">{fmtMoney(r.total)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <Button size="sm" variant="outline" onClick={() => openStatement(r.customer_id)}
                                                    data-testid={`statement-btn-${r.customer_id}`}>
                                                <FileText className="w-3.5 h-3.5 mr-1" />Statement
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Sheet open={!!statement} onOpenChange={(open) => !open && setStatement(null)}>
                <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="statement-sheet">
                    {statement && (
                        <StatementView
                            statement={statement}
                            onChangeStatus={(inv) => setStatusInvoice(inv)}
                        />
                    )}
                </SheetContent>
            </Sheet>

            <InvoiceStatusDialog
                open={!!statusInvoice}
                onOpenChange={(v) => !v && setStatusInvoice(null)}
                invoice={statusInvoice}
                onUpdated={refreshStatement}
            />
        </div>
    );
}

function StatementView({ statement, onChangeStatus }) {
    const { customer, invoices, payments, summary, as_of } = statement;
    return (
        <>
            <SheetHeader>
                <div className="overline">Statement of account</div>
                <SheetTitle className="font-heading text-2xl">{customer.name}</SheetTitle>
                <SheetDescription>
                    Account {customer.account_number} · As of {fmtDate(as_of)}
                </SheetDescription>
            </SheetHeader>
            <div className="mt-4 flex gap-2" data-testid="statement-actions">
                <Button variant="outline" size="sm" onClick={() => window.print()}
                        data-testid="statement-print" aria-label="Print statement">
                    <Printer className="w-4 h-4 mr-1" />Print
                </Button>
                {customer.email && (
                    <Button variant="outline" size="sm" data-testid="statement-email"
                            aria-label="Email statement to customer"
                            onClick={() => toast.info(`Statement email queued to ${customer.email}`)}>
                        <Mail className="w-4 h-4 mr-1" />Email
                    </Button>
                )}
            </div>

            <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="p-3 rounded-md border border-border">
                    <div className="overline mb-1">Open balance</div>
                    <div className="font-heading text-2xl font-bold tabular">{fmtMoney(summary.open_balance)}</div>
                </div>
                <div className="p-3 rounded-md border border-border">
                    <div className="overline mb-1">Lifetime paid</div>
                    <div className="font-heading text-2xl font-bold tabular">{fmtMoney(summary.lifetime_paid)}</div>
                </div>
                <div className="p-3 rounded-md border border-border">
                    <div className="overline mb-1">Invoices</div>
                    <div className="font-heading text-2xl font-bold tabular">{summary.invoice_count}</div>
                </div>
            </div>

            <div className="mt-6">
                <div className="overline mb-2">Invoices</div>
                <div className="border border-border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="surface">
                        <tr className="border-b border-border">
                            <th className="text-left px-3 py-2 overline">Number</th>
                            <th className="text-left px-3 py-2 overline">Issued</th>
                            <th className="text-left px-3 py-2 overline">Due</th>
                            <th className="text-right px-3 py-2 overline">Total</th>
                            <th className="text-right px-3 py-2 overline">Balance</th>
                            <th className="text-left px-3 py-2 overline">Status</th>
                            <th className="text-right px-3 py-2 overline"></th>
                        </tr>
                        </thead>
                        <tbody>
                        {invoices.map((inv) => (
                            <tr key={inv.id} className="border-b border-border">
                                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtDate(inv.issued_date)}</td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtDate(inv.due_date)}</td>
                                <td className="px-3 py-2 text-right tabular">{fmtMoney(inv.total)}</td>
                                <td className="px-3 py-2 text-right tabular font-medium">{fmtMoney(inv.balance)}</td>
                                <td className="px-3 py-2"><StatusPill status={inv.status} /></td>
                                <td className="px-3 py-2 text-right">
                                    <Button
                                        variant="ghost" size="sm"
                                        data-testid={`ar-inv-change-status-${inv.id}`}
                                        onClick={() => onChangeStatus?.(inv)}
                                    >
                                        Change status
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {payments.length > 0 && (
                <div className="mt-6">
                    <div className="overline mb-2">Payments</div>
                    <div className="border border-border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="surface">
                            <tr className="border-b border-border">
                                <th className="text-left px-3 py-2 overline">Date</th>
                                <th className="text-left px-3 py-2 overline">Method</th>
                                <th className="text-right px-3 py-2 overline">Amount</th>
                            </tr>
                            </thead>
                            <tbody>
                            {payments.map((p) => (
                                <tr key={p.id} className="border-b border-border">
                                    <td className="px-3 py-2 text-muted-foreground">{fmtDate(p.created_at)}</td>
                                    <td className="px-3 py-2">{p.metadata?.source === "autopay" ? "AutoPay" : "Online"}</td>
                                    <td className="px-3 py-2 text-right tabular">{fmtMoney(p.amount)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}
